import { useEffect, useCallback, useState } from 'react';

type View = 'overview' | 'macro' | 'macro-monitor' | 'monetary' | 'trade' | 'fdi' | 'sectors' | 'peers' | 'events' | 'natex-score' | 'settings';

const views: View[] = ['overview', 'natex-score', 'macro', 'macro-monitor', 'monetary', 'trade', 'fdi', 'sectors', 'peers', 'events', 'settings'];

interface UseKeyboardShortcutsProps {
  currentView: View;
  onViewChange: (view: View) => void;
  onOpenSearch?: () => void;
}

interface UseKeyboardShortcutsReturn {
  showShortcutsHelp: boolean;
  setShowShortcutsHelp: (show: boolean) => void;
}

export const useKeyboardShortcuts = ({
  currentView,
  onViewChange,
  onOpenSearch,
}: UseKeyboardShortcutsProps): UseKeyboardShortcutsReturn => {
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Ignore if typing in an input
    const target = e.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }

    // ⌘K / Ctrl+K - Open search
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      onOpenSearch?.();
      return;
    }

    // ? - Show keyboard shortcuts help
    if (e.key === '?' && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      setShowShortcutsHelp(prev => !prev);
      return;
    }

    // Escape - Close help
    if (e.key === 'Escape') {
      setShowShortcutsHelp(false);
      return;
    }

    // Number keys 1-9 - Quick navigate to views
    const num = parseInt(e.key);
    if (!e.metaKey && !e.ctrlKey && num >= 1 && num <= 9) {
      e.preventDefault();
      onViewChange(views[num - 1]);
      return;
    }

    // Arrow Left/Right - Navigate between views
    if (e.key === 'ArrowLeft' && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      const currentIdx = views.indexOf(currentView);
      const prevIdx = currentIdx > 0 ? currentIdx - 1 : views.length - 1;
      onViewChange(views[prevIdx]);
      return;
    }

    if (e.key === 'ArrowRight' && !e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      const currentIdx = views.indexOf(currentView);
      const nextIdx = currentIdx < views.length - 1 ? currentIdx + 1 : 0;
      onViewChange(views[nextIdx]);
      return;
    }

    // G then O/M/F/S/P/E - Vim-style navigation
    // (Would need more complex state tracking for this)
  }, [currentView, onViewChange, onOpenSearch]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  return {
    showShortcutsHelp,
    setShowShortcutsHelp,
  };
};

export default useKeyboardShortcuts;
